import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormRecord,
  ValidatorFn,
} from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  forkJoin,
  from,
  merge,
  Subject,
  Subscription,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import {
  AVAILABLE_LANGUAGES,
  Language,
  MiddlewareSDK,
  MockAvailableLocales,
  MockSdkService,
  SDK,
} from '@plugins/middleware-sdk';
import { MultilanguageContent } from '@plugins/shared/types/multilanguage';
import { isNonNullish } from '@plugins/shared/utils/assertFunctions';

import { MultilanguageAddData, MultilanguageLanguages } from './types';

@Injectable()
abstract class MultilanguageAbstractService implements OnDestroy {
  protected abstract sdk: MiddlewareSDK;
  protected _limitedLanguages$ = new BehaviorSubject<string[]>([]);
  limitedLanguages$ = this._limitedLanguages$.asObservable();

  protected _availableLanguages$ = new BehaviorSubject<string[] | null>(null);
  availableLanguages$ = this._availableLanguages$
    .asObservable()
    .pipe(filter((val) => !!val));

  protected _defaultLanguage$ = new BehaviorSubject<string>('');
  defaultLanguage$ = this._defaultLanguage$
    .asObservable()
    .pipe(filter((val) => !!val));

  protected _languages$ = new BehaviorSubject<MultilanguageLanguages | null>(
    null,
  );
  languages$ = this._languages$.asObservable().pipe(filter((val) => !!val));

  protected selectLanguage$ = new Subject<string>();

  protected _currentLanguage$ = new BehaviorSubject<string>('');
  currentLanguage$ = this._currentLanguage$
    .asObservable()
    .pipe(filter((val) => !!val));

  protected _preferredLanguage$ = new BehaviorSubject<string>('');
  preferredLanguage$ = this._preferredLanguage$
    .asObservable()
    .pipe(filter((val) => !!val));

  protected _addLanguage$ = new Subject<MultilanguageAddData>();
  addLanguage$ = this._addLanguage$.asObservable();

  protected _removeLanguage$ = new Subject<string>();
  removeLanguage$ = this._removeLanguage$.asObservable();

  removeDisabled$ = this.languages$.pipe(
    map(({ added }) => added.length === 1),
  );

  addDisabled$ = this.languages$.pipe(
    map(({ remaining }) => !remaining.length),
  );

  protected _showAdd$ = new BehaviorSubject<boolean>(true);
  showAdd$ = combineLatest([
    this._showAdd$.asObservable().pipe(startWith(this._showAdd$.value)),
    this.languages$.pipe(map(({ remaining }) => !!remaining.length)),
  ]).pipe(map(([show, remaining]) => show && remaining));

  protected _errors$ = new BehaviorSubject<Record<string, boolean>>({});
  errors$ = this._errors$.asObservable().pipe(startWith(this._errors$.value));

  protected destroy$ = new Subject<void>();

  get availableLanguages() {
    return this._availableLanguages$.value ?? [];
  }

  get languages() {
    return this._languages$.value;
  }

  get addedLanguages() {
    return this._languages$.value?.added ?? [];
  }

  get remainingLanguages() {
    return this._languages$.value?.remaining ?? [];
  }

  get limitedLanguages() {
    return this._limitedLanguages$.value;
  }

  get defaultLanguage() {
    return this._defaultLanguage$.value;
  }

  get preferredLanguage() {
    return this._preferredLanguage$.value;
  }

  get currentLanguage() {
    return this._currentLanguage$.value;
  }

  set errors(val: string[]) {
    this._errors$.next(
      val.reduce<Record<string, true>>((acc, curr) => {
        acc[curr] = true;
        return acc;
      }, {}),
    );
  }

  get errors() {
    return Object.keys(this._errors$.value);
  }

  protected initializeTrackingSubscription(): Subscription {
    return forkJoin([this.getAvailableLanguages(), this.getDefaultLanguage()])
      .pipe(
        tap(([availableLanguages, defaultLanguage]) => {
          this._availableLanguages$.next(availableLanguages);
          this._defaultLanguage$.next(defaultLanguage);
        }),
        switchMap(() =>
          merge(
            this.trackLanguages(),
            this.trackCurrentLanguage(),
            this.trackPreferredLanguage(),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  protected getAvailableLanguages() {
    return from(this.sdk.getAvailableLanguages()).pipe(
      map((languages) => languages.map((l) => l.locale)),
    );
  }

  protected getDefaultLanguage() {
    return from(this.sdk.getDefaultLanguage()).pipe(
      map(({ locale }) => locale),
    );
  }

  protected trackLanguages() {
    return combineLatest([
      this.availableLanguages$,
      this.limitedLanguages$,
    ]).pipe(
      map(([available, limited]) => {
        if (!limited.length) {
          return {
            added: [],
            remaining: [...available],
          };
        }
        const added = [...limited];
        const remaining = available.filter((l) => !added.includes(l));
        return { added, remaining };
      }),
      tap((languages) => {
        this._languages$.next(languages);
      }),
    );
  }

  protected trackCurrentLanguage() {
    return combineLatest([this.languages$, this.defaultLanguage$]).pipe(
      switchMap(([{ added }, fallback]) =>
        this.selectLanguage$.pipe(
          startWith(this.currentLanguage),
          map((language) =>
            language && added.includes(language)
              ? language
              : fallback && added.includes(fallback)
                ? fallback
                : added[0],
          ),
        ),
      ),
      distinctUntilChanged(),
      tap((language) => {
        this._currentLanguage$.next(language);
      }),
    );
  }

  protected trackPreferredLanguage() {
    return this.languages$.pipe(
      switchMap((languages) => this.sdk.getPreferredLanguage(languages.added)),
      distinctUntilChanged(),
      tap((language) => {
        this._preferredLanguage$.next(language);
      }),
    );
  }

  /** Set languages that are currently active in the application */
  setLimitedLanguages(languages: string[]) {
    this._limitedLanguages$.next(languages);
  }

  selectLanguage(language: string) {
    if (this.addedLanguages.includes(language)) {
      this.selectLanguage$.next(language);
    }
  }

  showAddLanguage(show: boolean) {
    this._showAdd$.next(show);
  }

  initLanguageValue(defaultValue = '') {
    return this.addedLanguages.reduce<Record<string, string>>((acc, curr) => {
      acc[curr] = defaultValue;
      return acc;
    }, {});
  }

  createLanguageControl<T>(
    value: MultilanguageContent<T>,
    validators?: ValidatorFn | ValidatorFn[],
    commonValidators?: ValidatorFn | ValidatorFn[],
  ) {
    const record = new FormRecord<FormControl<T>>({}, commonValidators);
    for (const lang of Object.keys(value)) {
      record.addControl(
        lang,
        new FormControl<T>(value[lang], { validators, nonNullable: true }),
      );
    }
    return record;
  }

  addLanguageFormControl(
    group: FormGroup,
    language: string,
    base?: string,
    validators?: ValidatorFn | ValidatorFn[],
  ) {
    const baseControl = base ? group.get(base) : null;
    group.addControl(
      language,
      new FormControl(
        baseControl?.value ?? null,
        baseControl?.validator ?? validators,
      ),
    );
  }

  removeLanguageFormControl(group: FormGroup, language: string) {
    group.removeControl(language);
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  updateControl<T, Group extends AbstractControl>(
    group: Group,
    value: MultilanguageContent<T>,
    validators?: ValidatorFn | ValidatorFn[],
  ) {
    if (!(group instanceof FormGroup || group instanceof FormRecord)) {
      return;
    }
    for (const lang of Object.keys(value)) {
      const control = group.controls[lang] as FormControl<T> | undefined;
      if (control) {
        control.setValue(value[lang], { emitEvent: false });
      } else {
        group.addControl(
          lang,
          new FormControl(value[lang], { validators, nonNullable: true }),
        );
      }
    }
  }

  addMultilanguageValue<T>(
    value: T | T[],
    language: string | string[],
    base?: string,
  ): T | T[] {
    if (!value || typeof value !== 'object' || value instanceof Date) {
      return value;
    }
    if (
      !Array.isArray(value) &&
      (!base || Object.hasOwnProperty.call(value, base))
    ) {
      if (Array.isArray(language)) {
        language.forEach((l) => {
          value = {
            ...(value as T),
            [l]: base ? (value as T)[base as keyof T] : null,
          };
        });
      } else {
        value = {
          ...value,
          [language]: base ? value[base as keyof T] : null,
        };
      }
    }
    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.map(
          (v) => this.addMultilanguageValue(v, language, base) as T,
        );
      } else {
        return Object.entries(value).reduce((acc, [k, v]) => {
          (acc as T)[k as keyof T] = this.addMultilanguageValue(
            v,
            language,
            base,
          ) as T[keyof T];
          return acc;
        }, {}) as T;
      }
    }
    return value;
  }

  removeMultilanguageValue<T>(value: T | T[], language: keyof T): T | T[] {
    if (!value || typeof value !== 'object' || value instanceof Date) {
      return value;
    }
    if (!Array.isArray(value) && Object.hasOwnProperty.call(value, language)) {
      value = { ...value };
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete value[language];
    }
    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.map(
          (v) => this.removeMultilanguageValue(v, language) as T,
        );
      } else {
        return Object.entries(value).reduce((acc, [k, v]) => {
          (acc as T)[k as keyof T] = this.removeMultilanguageValue(
            v as T,
            language,
          ) as T[keyof T];
          return acc;
        }, {}) as T;
      }
    }
    return value;
  }

  onAddLanguage(data: MultilanguageAddData) {
    this._addLanguage$.next(data);
  }

  onRemoveLanguage(language: string) {
    this._removeLanguage$.next(language);
  }

  addLanguage(language: string, select = true) {
    if (!this.remainingLanguages.includes(language)) {
      return;
    }
    this.setLimitedLanguages([...this.limitedLanguages, language]);
    if (select) {
      this.selectLanguage(language);
    }
  }

  removeLanguage(language: string) {
    this.setLimitedLanguages(
      this.limitedLanguages.filter((l) => l !== language),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

@Injectable()
export class MultilanguageService extends MultilanguageAbstractService {
  protected readonly sdk = inject(SDK);
  constructor() {
    super();
    this.initializeTrackingSubscription();
  }
}

export class MultilanguageServiceMock extends MultilanguageAbstractService {
  readonly mockSdk = new MockSdkService();
  protected readonly sdk = this.mockSdk as unknown as MiddlewareSDK;
  constructor(
    settings?: Partial<{
      availableLanguages: MockAvailableLocales[];
      /** Represents languages that have been picked from all
       * available locales as being active in the plugin.
       * This corresponds to the `limitedLanguages` property.
       *
       * If not provided, either the passed argument for preferred or
       * default language or the first of available languages
       * will be set. If none are available,`en_US` will be set.*/
      activeLangauges: MockAvailableLocales[];
      defaultLanguage: MockAvailableLocales;
      preferredLanguage: MockAvailableLocales;
    }>,
  ) {
    super();
    const matchLang = ({ locale }: Language, fullLang: string) =>
      locale === fullLang;
    if (!settings) {
      this.initializeTrackingSubscription();
      return;
    }

    const {
      availableLanguages,
      defaultLanguage,
      preferredLanguage,
      activeLangauges,
    } = settings;
    if (isNonNullish<typeof availableLanguages>(availableLanguages)) {
      this.mockSdk.availableLanguages = AVAILABLE_LANGUAGES.filter((lang) =>
        availableLanguages.some((entry) => matchLang(lang, entry)),
      );
    }

    if (isNonNullish<typeof defaultLanguage>(defaultLanguage)) {
      this.mockSdk.defaultLanguage =
        AVAILABLE_LANGUAGES.find((lang) => matchLang(lang, defaultLanguage)) ??
        this.mockSdk.defaultLanguage;
    }
    if (isNonNullish<typeof preferredLanguage>(preferredLanguage)) {
      this.mockSdk.preferredLanguage =
        AVAILABLE_LANGUAGES.find((lang) => matchLang(lang, preferredLanguage))
          ?.locale ?? this.mockSdk.preferredLanguage;
    }

    this.setLimitedLanguages(
      activeLangauges ??
        (preferredLanguage
          ? [preferredLanguage]
          : defaultLanguage
            ? [defaultLanguage]
            : (availableLanguages ?? ['en_US'])),
    );
    this.initializeTrackingSubscription();
  }
}
